import { Datetime, Field, Select } from "components/shared/form";
import { vehicleOwnershipBasisDocument } from "helpers/models";
import { requiresOwnershipBasisDocuments } from "helpers/vehicles/ownership";
import React, { useEffect, useState } from "react";

let nextOwnershipDocumentKey = 0;
const createOwnershipDocumentKey = () => `new-document-${nextOwnershipDocumentKey++}`;
const emptyDocuments = [];
const alwaysRender = () => true;

/** Render vehicle characteristics, ownership type, and editable ownership-basis documents. */
const VehicleOwnership = ({
    carrying,
    volume,
    ownershipType,
    ownershipBasisDocuments,
    data,
    setValue,
    renderField,
    renderSelect,
    disabled = false,
    organizationName,
    ownershipSectionClassName = "bg-f9-33 border-left border-primary pl-3 pr-2 py-2 mt-2 mb-3",
    ownershipBasisDocumentsVisible,
    shouldRenderDocument = alwaysRender,
    shouldRenderDocumentField = alwaysRender,
    shouldRenderField = alwaysRender,
}) => {
    const documents = data.ownershipBasisDocuments || emptyDocuments;
    const [documentKeys, setDocumentKeys] = useState(() =>
        documents.map((document) => (document.id ? `document-${document.id}` : createOwnershipDocumentKey())),
    );
    const requiresDocuments = requiresOwnershipBasisDocuments(data.ownershipType);
    const showOwnershipBasisDocuments =
        ownershipBasisDocumentsVisible === undefined ? requiresDocuments : ownershipBasisDocumentsVisible;
    const ownershipTypeLabel = ownershipType.options.find(({ value }) => value === data.ownershipType)?.label;

    useEffect(() => {
        setDocumentKeys((currentKeys) =>
            documents.map(
                (document, index) =>
                    (document.id ? `document-${document.id}` : currentKeys[index]) || createOwnershipDocumentKey(),
            ),
        );
    }, [documents]);

    const setDocumentValue = (index, { name, value }) => {
        const nextDocuments = documents.map((document, documentIndex) =>
            documentIndex === index
                ? {
                      ...document,
                      [name]: value,
                      unfilledRequiredProperties: (document.unfilledRequiredProperties || []).filter(
                          (property) => property.toLowerCase() !== name.toLowerCase(),
                      ),
                      ...(name === "counterpartyId" ? { counterparty: null } : {}),
                      ...(name === "legalPersonId" ? { legalPerson: null } : {}),
                  }
                : document,
        );
        setValue({ name: "ownershipBasisDocuments", value: nextDocuments });
    };

    const removeDocument = (index) => {
        setDocumentKeys((currentKeys) => currentKeys.filter((_, documentIndex) => documentIndex !== index));
        setValue({
            name: "ownershipBasisDocuments",
            value: documents.filter((_, documentIndex) => documentIndex !== index),
        });
    };

    const addDocument = () => {
        setDocumentKeys((currentKeys) => [...currentKeys, createOwnershipDocumentKey()]);
        setValue({
            name: "ownershipBasisDocuments",
            value: [...documents, { ...vehicleOwnershipBasisDocument }],
        });
    };

    const renderDocument = (document, index, key) => {
        const config = ownershipBasisDocuments.config;
        const onChange = (value) => setDocumentValue(index, value);
        const isFieldVisible = (name) => shouldRenderDocumentField(document, index, name);
        const compactField = (fieldConfig) => ({
            ...fieldConfig,
            bWrapper: "form-group mb-2",
            fieldWrapper: "w-100",
            inputProps: { ...fieldConfig.inputProps, disabled },
            labelWrapper: "d-block small text-muted mb-1",
            wrapper: "w-100",
        });
        const fieldError = (name) =>
            (document.unfilledRequiredProperties || []).some(
                (property) => property.toLowerCase() === name.toLowerCase(),
            )
                ? "Обязательное поле"
                : "";
        const documentErrors = [
            ...new Set([
                ...(document.errorMessages || []),
                ...(document.organization?.errorMessages || []),
                ...(document.legalPerson?.errorMessages || []),
                ...(document.counterparty?.errorMessages || []),
            ]),
        ];

        return (
            <div className="bg-ff-2c border rounded p-2 mb-2" key={key}>
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <strong className="small">Документ {index + 1}</strong>
                    {!disabled && (
                        <button
                            className="btn btn-link btn-xs text-danger p-0"
                            onClick={() => removeDocument(index)}
                            type="button"
                        >
                            Удалить
                        </button>
                    )}
                </div>
                {!!documentErrors.length && (
                    <div className="alert alert-danger py-1 px-2 small mb-2">
                        {documentErrors.map((error) => (
                            <div key={error}>{error}</div>
                        ))}
                    </div>
                )}
                <div className="row">
                    {isFieldVisible("name") && (
                        <div className="col-lg-6">
                            <Field
                                {...compactField(config.name)}
                                error={fieldError("name")}
                                index={index}
                                onChange={onChange}
                                value={document.name}
                            />
                        </div>
                    )}
                    {isFieldVisible("number") && (
                        <div className="col-sm-6 col-lg-3">
                            <Field
                                {...compactField(config.number)}
                                error={fieldError("number")}
                                index={index}
                                onChange={onChange}
                                value={document.number}
                            />
                        </div>
                    )}
                    {isFieldVisible("date") && (
                        <div className="col-sm-6 col-lg-3">
                            <Datetime
                                {...compactField(config.date)}
                                index={index}
                                onChange={onChange}
                                value={document.date}
                            />
                        </div>
                    )}
                    {isFieldVisible("legalPersonId") && (
                        <div className="col-lg-6">
                            <Select
                                {...compactField(config.legalPersonId)}
                                index={index}
                                onChange={onChange}
                                placeholder={organizationName || "Текущая транспортная организация"}
                                value={document.legalPersonId}
                            />
                            <small className="d-block text-muted mb-2">Пусто — текущая транспортная организация</small>
                        </div>
                    )}
                    {isFieldVisible("counterpartyId") && (
                        <div className="col-lg-6">
                            <Select
                                {...compactField(config.counterpartyId)}
                                error={fieldError("counterpartyId")}
                                index={index}
                                onChange={onChange}
                                value={document.counterpartyId}
                            />
                        </div>
                    )}
                    {isFieldVisible("externalId") && (
                        <div className="col-lg-4">
                            <Field
                                {...compactField(config.externalId)}
                                index={index}
                                onChange={onChange}
                                value={document.externalId}
                            />
                        </div>
                    )}
                    {isFieldVisible("additionalInfo") && (
                        <div className="col-lg-8">
                            <Field
                                {...compactField(config.additionalInfo)}
                                index={index}
                                onChange={onChange}
                                value={document.additionalInfo}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            {shouldRenderField("carrying") && renderField(carrying)}
            {shouldRenderField("volume") && renderField(volume)}
            {shouldRenderField("ownershipType") && renderSelect(ownershipType)}
            {showOwnershipBasisDocuments && (
                <section className={ownershipSectionClassName}>
                    <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
                        <div className="mr-3">
                            <h6 className="mb-0">Основание владения</h6>
                            <small className="text-muted">Для типа владения «{ownershipTypeLabel}»</small>
                        </div>
                        {!disabled && (
                            <button className="btn btn-xs btn-outline-primary" onClick={addDocument} type="button">
                                Добавить документ
                            </button>
                        )}
                    </div>
                    {!documents.length && (
                        <small className={disabled ? "d-block text-muted py-2" : "d-block text-danger py-2"}>
                            Документы-основания не указаны
                        </small>
                    )}
                    {documents.map((document, index) =>
                        shouldRenderDocument(document, index)
                            ? renderDocument(
                                  document,
                                  index,
                                  document.id ? `document-${document.id}` : documentKeys[index],
                              )
                            : null,
                    )}
                </section>
            )}
        </>
    );
};

export default VehicleOwnership;
